/**
 * Servizio condiviso per il refresh del JWT access token.
 *
 * Fix B9 (Round 2 Wave A discrepanza Auth+RBAC): prima il refresh logic
 * era duplicato in due moduli separati — `axiosInstance.ts` (REST admin)
 * e `authLink.ts` (Apollo GraphQL) — ognuno con il proprio `isRefreshing`
 * lock locale. Effetto: richieste REST + GraphQL concorrenti che ricevono
 * 401 contemporaneamente potevano triggerare DUE refresh paralleli al
 * server, il primo invalidava il vecchio refresh token, e il secondo
 * falliva con "Invalid refresh token" → logout dell'utente.
 *
 * Ora entrambi i layer importano `refreshAccessToken()` da questo modulo
 * che implementa un singleton promise: se un refresh è già in corso,
 * il chiamante successivo attende lo stesso promise invece di lanciarne
 * uno nuovo.
 */

import axios from 'axios';

const GRAPHQL_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/graphql';

// Singleton promise condiviso: null quando nessun refresh è in corso.
let refreshPromise: Promise<string> | null = null;

/**
 * Esegue il refresh del JWT access token chiamando la mutation
 * GraphQL `refreshToken`. Se un refresh è già in corso, ritorna lo
 * stesso promise (NO chiamate duplicate al server).
 *
 * Side effects:
 * - Su successo: aggiorna `localStorage.accessToken` e
 *   `localStorage.refreshToken` con i nuovi valori ruotati.
 * - Su fallimento: rimuove entrambi i token da localStorage (sessione
 *   da considerare scaduta; chi chiama deve gestire il redirect a login).
 *
 * @returns Il nuovo access token (stringa JWT).
 * @throws Error se non c'e refresh token in localStorage o se la
 *   mutation server fallisce.
 */
export const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post(GRAPHQL_URL, {
        query: `
          mutation RefreshToken($refreshToken: String!) {
            refreshToken(refreshToken: $refreshToken) {
              accessToken
              refreshToken
            }
          }
        `,
        variables: { refreshToken }
      });

      const result = response.data?.data?.refreshToken;
      if (!result?.accessToken) {
        throw new Error('Token refresh failed');
      }

      const { accessToken, refreshToken: newRefreshToken } = result;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);

      return accessToken;
    } catch (error) {
      // Rimuovi token non validi: la sessione e da considerare scaduta.
      // Il redirect a /admin/login resta responsabilita del chiamante
      // (axiosInstance o authLink), perche il path corretto cambia in
      // base al contesto (admin vs associate).
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw error;
    } finally {
      // Reset il singleton: il prossimo refresh ripartira da zero.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
